import type * as React from "react";

/** Interactive, toggleable pill (filters, multi-select). Pressed/selected fills with the tone's soft color + colored dot + colored border. */
export interface ChipProps {
  children?: React.ReactNode;
  /** Color family when selected. @default "neutral" */
  tone?: "neutral" | "brand" | "person" | "company" | "event" | "opportunity";
  /** Pressed / active state. @default false */
  selected?: boolean;
  /** Show the leading color dot. @default true */
  dot?: boolean;
  /** Optional leading element (replaces nothing; sits before the dot). */
  leading?: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  style?: React.CSSProperties;
}

const TONES: Record<NonNullable<ChipProps["tone"]>, [string, string, string, string]> = {
  neutral: ["var(--warm-400)", "var(--surface-sunken)", "var(--text-body)", "var(--border-default)"],
  brand: ["var(--accent)", "var(--accent-soft)", "var(--accent-soft-text)", "var(--accent)"],
  person: ["var(--entity-person)", "var(--entity-person-soft)", "var(--entity-person-text)", "var(--entity-person)"],
  company: ["var(--entity-company)", "var(--entity-company-soft)", "var(--entity-company-text)", "var(--entity-company)"],
  event: ["var(--entity-event)", "var(--entity-event-soft)", "var(--entity-event-text)", "var(--entity-event)"],
  opportunity: ["var(--entity-opportunity)", "var(--entity-opportunity-soft)", "var(--entity-opportunity-text)", "var(--entity-opportunity)"],
};

export function Chip({ children, tone = "neutral", selected = false, dot = true, leading, onClick, style }: ChipProps) {
  const [c, soft, text, border] = TONES[tone] || TONES.neutral;
  const clickable = !!onClick;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={clickable ? selected : undefined}
      style={
        {
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          padding: "6px 12px",
          borderRadius: "var(--radius-pill)",
          cursor: clickable ? "pointer" : "default",
          fontFamily: "var(--font-sans)",
          fontSize: "var(--text-body-sm)",
          fontWeight: "var(--fw-bold)",
          whiteSpace: "nowrap",
          border: `1px solid ${selected ? border : "var(--border-default)"}`,
          background: selected ? soft : "var(--surface-card)",
          color: selected ? text : "var(--text-muted)",
          transition:
            "background var(--duration-fast) var(--ease-out), border-color var(--duration-fast) var(--ease-out), color var(--duration-fast) var(--ease-out)",
          ...style,
        } as React.CSSProperties
      }
    >
      {leading}
      {dot && (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: selected ? c : "var(--warm-300)",
            flex: "none",
          }}
        />
      )}
      {children}
    </button>
  );
}
